import type {
  AnalysisRequestInput,
  AnalysisResult,
  ApiResponse,
  AuthTokens,
  ChatMessage,
  ChatResponse,
  Chip,
  ChipPlan,
  EffectiveOwnership,
  FdrGrid,
  GameweekMeta,
  LeagueInsights,
  LeagueStandingsPage,
  LeagueSummary,
  LedgerAggregate,
  LoginInput,
  PlayerAssessment,
  PlayerComparison,
  Position,
  PublicLedgerAggregate,
  RankContext,
  RefreshInput,
  RegisterInput,
  RivalComparison,
  ScoredRecommendation,
  Squad,
  SquadInput,
  SquadPlayer,
  SquadValidation,
  TransferPlan,
  User,
} from '@fantasy/types';
import { reportUnexpectedError } from './lib/sentry.ts';

/**
 * In dev the Vite proxy forwards /api to the API server, so a relative base
 * works in both modes and the built container needs no origin baked in.
 */
const BASE = import.meta.env.VITE_API_URL ?? '';

/** Persisted so a page reload can restore a session without asking the user
 * to log in again — see `restoreSession` in AuthContext. The access token is
 * deliberately NOT kept here: it lives only in memory, below, to limit what
 * an XSS payload reading localStorage could steal. */
const REFRESH_TOKEN_KEY = 'fantasy-manager:refresh-token';

let accessToken: string | null = null;

/** Notified when a refresh-and-retry fails, so the app can drop back to the
 * login screen. Set by `AuthProvider`; there is only ever one at a time. */
let onAuthExpired: (() => void) | null = null;

export function setAuthExpiredListener(listener: (() => void) | null): void {
  onAuthExpired = listener;
}

/** Read fresh each time rather than cached, so a refresh token written to
 * storage after this module first loaded (or cleared out from under it) is
 * always picked up. */
export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

/** Records a freshly issued token pair: access token in memory, refresh
 * token persisted so it survives a reload. */
export function setSession(tokens: AuthTokens): void {
  accessToken = tokens.accessToken;
  localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
}

export function clearSession(): void {
  accessToken = null;
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

export class ApiRequestError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly details?: Array<{ path: string; message: string }>,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

interface RequestOptions {
  /** Set on the retried call after a refresh, so a second 401 in a row is
   * surfaced instead of looping. */
  isRetry?: boolean;
}

async function doFetch(path: string, init?: RequestInit): Promise<Response> {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...init?.headers,
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
  };

  try {
    return await fetch(`${BASE}${path}`, { ...init, headers });
  } catch {
    throw new ApiRequestError('NETWORK', 'Could not reach the server. Is the API running?');
  }
}

async function request<T>(path: string, init?: RequestInit, options: RequestOptions = {}): Promise<T> {
  try {
    const response = await doFetch(path, init);

    // A session that looked valid can still be rejected (expired access
    // token) — try one silent refresh-and-retry before giving up on it.
    if (response.status === 401 && accessToken && !options.isRetry) {
      const refreshed = await attemptRefresh();
      if (refreshed) return await request<T>(path, init, { isRetry: true });

      clearSession();
      onAuthExpired?.();
    }

    if (response.status === 204) return undefined as T;

    const body = (await response.json()) as ApiResponse<T>;
    if (!body.ok) {
      throw new ApiRequestError(body.error.code, body.error.message, body.error.details);
    }
    return body.data;
  } catch (error) {
    // ApiRequestError is every failure mode this function already
    // classified above (a real network problem, the API's own error
    // envelope) — every calling component already handles it with a
    // friendly message, so it isn't a bug worth reporting. Anything else
    // reaching here (e.g. response.json() choking on a malformed body) is
    // exactly the "this shouldn't have happened" case — every one of this
    // app's ~15 API call sites routes through this single function, so this
    // is the one place that needs reporting, not each of them individually.
    if (error instanceof ApiRequestError) throw error;
    reportUnexpectedError(error, { path });
    throw new ApiRequestError('INTERNAL', 'Something went wrong. Please try again.');
  }
}

/** Single-flight: concurrent callers share the same attempt rather than each
 * rotating the refresh token — the token is single-use server-side, so two
 * simultaneous callers presenting the same one would have the loser's
 * request rejected as already-used. Exported so `AuthContext`'s mount-time
 * session restore goes through the same guard as the 401-retry path below;
 * React StrictMode double-invokes that mount effect in dev, which used to
 * fire two real refresh calls with the same stored token — the loser's
 * failure then cleared the winner's freshly-rotated session, so a session
 * that briefly restored fine would still show as logged out on the very
 * next reload. */
let refreshInFlight: Promise<boolean> | null = null;

export async function attemptRefresh(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = performRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

async function performRefresh(): Promise<boolean> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;
  try {
    const { tokens } = await api.auth.refresh(refreshToken);
    setSession(tokens);
    return true;
  } catch {
    return false;
  }
}

export interface CreateSquadResponse {
  squad: Squad;
  validation: SquadValidation;
}

/** A screenshot-extracted player whose name fragment + club couldn't be
 * confidently matched against our ingested player pool. Reported back rather
 * than silently dropped or guessed at. */
export interface UnmatchedScreenshotPlayer {
  nameFragment: string;
  club: string;
}

export interface ScreenshotImportResponse {
  /** `null` when any extracted player couldn't be matched — see `unmatched`. */
  squad: SquadInput | null;
  unmatched: UnmatchedScreenshotPlayer[];
  /** Every player that did match successfully, in extraction order —
   * populated whenever `unmatched` is non-empty, so a single bad fragment
   * doesn't throw away the rest of an otherwise-good extraction. Empty when
   * `squad` is returned instead. */
  matched: SquadPlayer[];
}

export interface ImportScreenshotInput {
  /** Base64-encoded image data, no `data:` URL prefix. */
  image: string;
  mediaType: 'image/png' | 'image/jpeg' | 'image/webp';
  gameweek: number;
}

/** A player as returned by the search endpoint — real data from our ingested
 * pool, not free text, so a `PlayerPicker` selection can't disagree with the
 * player's actual club/position/price. */
export interface PlayerSearchResult {
  fplId: number;
  name: string;
  position: 'GKP' | 'DEF' | 'MID' | 'FWD';
  club: string;
  price: number;
}

/** One squad player's live gameweek score, matched back onto `SquadPlayer`
 * by `fplId` — see `getLivePoints`. */
export interface LivePlayerPoints {
  fplId: number;
  points: number;
  minutes: number;
}

/** The gameweek total FPL itself reports for a linked squad, plus the chip
 * it had in play — `entry_history.points` from the manager's own picks,
 * already accounting for auto-substitutions, which the per-player join above
 * (and `estimateSquadPoints`) deliberately doesn't model. `null` for a squad
 * with no `entryId`, or when the lookup itself failed — either way there's
 * nothing to prefer over the estimate. See `getLivePoints`. */
export interface OfficialGameweekPoints {
  points: number;
  chip: Chip | null;
}

/** One player in a past gameweek's picks, as `getSquadHistory` returns it —
 * deliberately smaller than `SquadPlayer`: this is a read-only look-back
 * (T-129), not a squad, so it carries only what a look-back needs to show.
 * Mirrors `GameweekHistoryPlayer` in `squads.ts`. */
export interface GameweekHistoryPlayer {
  name: string;
  position: Position;
  isStarting: boolean;
  isCaptain: boolean;
  isViceCaptain: boolean;
  benchOrder?: number;
}

/** A past gameweek's picks for an FPL-linked squad, plus the points FPL
 * recorded for it — see `SquadHistory`. Never a `Squad`: nothing in this
 * shape can be saved, edited or analysed. Mirrors `GameweekHistory` in
 * `squads.ts`. */
export interface GameweekHistory {
  gameweek: number;
  /** `entry_history.points` for this gameweek — null when FPL's response
   * didn't carry it. */
  points: number | null;
  players: GameweekHistoryPlayer[];
}

/** A manager tracked by FPL entry id — identity only, no points; see
 * `getWatchedManagers`. */
export interface WatchedManager {
  entryId: number;
  teamName: string;
  managerName: string;
}

/** One watched manager's live gameweek score, matched back by `entryId` —
 * see `getWatchedManagersLivePoints`. */
export interface ManagerLivePoints {
  entryId: number;
  points: number;
  totalPoints: number;
}

/** One (entryId, gameweek) reading — the latest snapshot recorded for that
 * gameweek, accumulated quietly on every `getWatchedManagersLivePoints` call.
 * See `getWatchedManagersHistory`. */
export interface ManagerHistoryPoint {
  entryId: number;
  gameweek: number;
  points: number;
}

/** One gameweek's reading of the signed-in user's own squad-points estimate
 * — see `getWatchedManagersHistory` and `recordOwnSquadSnapshot`. */
export interface OwnSquadHistoryPoint {
  gameweek: number;
  points: number;
}

/** A player assessment as `getPublicSquad` returns it — `PlayerAssessment`
 * plus the injury/press-conference blurb the server adds only on this route
 * (see `publicSquad.ts`): a visitor reading this for free is exactly who
 * wants the news alongside the score, unlike the authenticated `analyse`
 * flow where it's prompt context the model reads but never returns. */
export interface PublicPlayerAssessment extends PlayerAssessment {
  news?: string;
}

export const api = {
  /** The gameweek to default the UI to — not a hardcoded guess, since that's
   * wrong the moment the season doesn't match it (pre-season, mid-week) —
   * plus the deadline countdown's own `deadline`, `phase` and `nextDeadline`
   * (T-074). Widened from `{ currentGameweek }` to the full `GameweekMeta`;
   * every existing caller only ever read `currentGameweek` off the result,
   * so this is additive. */
  currentGameweek: () => request<GameweekMeta>('/api/meta/gameweek'),

  /** Fixture-difficulty grid — clubs down the side, gameweeks across the top,
   * FPL's 1–5 difficulty per match, blanks and doubles represented honestly.
   * Reference data independent of any squad, same as `currentGameweek` —
   * `FdrGrid` fetches it on mount regardless of whether one is loaded.
   * `gameweeks` is the horizon width; omitted leaves it to the server's own
   * default. */
  getFdr: (gameweeks?: number) =>
    request<{ grid: FdrGrid }>(`/api/fixtures/fdr${gameweeks ? `?gameweeks=${gameweeks}` : ''}`),

  /** The free, signed-out squad read (T-088): one manager's picks pulled
   * straight from FPL, validated and scored — no account, no write anywhere
   * server-side. `gameweek` is the optional override a share link carries
   * (T-089) — omitted, the server resolves the entry's current gameweek,
   * exactly today's behaviour. See `PublicVerdict`. */
  getPublicSquad: (entryId: number, gameweek?: number) =>
    request<{ squad: Squad; validation: SquadValidation; assessments: PublicPlayerAssessment[] }>(
      `/api/public/squad/${entryId}${gameweek ? `?gameweek=${gameweek}` : ''}`,
    ),

  /** The deliberate second action on the same page — a real model call,
   * cached per entry id and gameweek server-side, rate-limited well below
   * the authenticated `analyse` above. `options` defaults to the server's
   * own defaults, same as `analyse`; `gameweek` is the same share-link
   * override `getPublicSquad` takes, so the verdict argues about the squad
   * that was actually fetched, not whatever gameweek is current now. */
  getPublicVerdict: (entryId: number, options: AnalysisRequestInput = {}, gameweek?: number) =>
    request<{ verdict: AnalysisResult }>(
      `/api/public/squad/${entryId}/verdict${gameweek ? `?gameweek=${gameweek}` : ''}`,
      {
        method: 'POST',
        body: JSON.stringify(options),
      },
    ),

  createSquad: (input: SquadInput) =>
    request<CreateSquadResponse>('/api/squads', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  getSquad: (id: string) => request<CreateSquadResponse>(`/api/squads/${id}`),

  /** Replaces a saved squad in place. Used to attach an FPL team id to a
   * squad that was built by hand or from a screenshot (see `LinkFplTeam`) —
   * the write itself only stores the id; the refresh that fills in chips,
   * bank and free transfers happens on the next `getSquad`. */
  updateSquad: (id: string, input: SquadInput) =>
    request<CreateSquadResponse>(`/api/squads/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),

  /** The explicit, manager-initiated counterpart to the passive refresh
   * `getSquad` already does on every load: replaces this squad's players and
   * gameweek in place with what FPL currently reports for its linked entry,
   * rather than leaving a manager stuck re-importing into a second, separate
   * squad to bring in a transfer made on the FPL site. Same squad id, same
   * name — see `SquadView`'s "Update to this gameweek" button. Rejects with
   * `VALIDATION_FAILED` for a squad with no `entryId`; there is nothing to
   * update it against. */
  updateToGameweek: (id: string) =>
    request<CreateSquadResponse>(`/api/squads/${id}/update-to-gameweek`, { method: 'POST' }),

  /** This account's squads, newest first — the server is the source of
   * truth for "your squads", not anything kept in this browser. */
  listSquads: (limit?: number) =>
    request<{ squads: Squad[] }>(`/api/squads${limit ? `?limit=${limit}` : ''}`),

  importScreenshot: (input: ImportScreenshotInput) =>
    request<ScreenshotImportResponse>('/api/import/screenshot', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  importFromEntry: (entryId: number) =>
    request<{ squad: SquadInput }>(`/api/import/${entryId}`, { method: 'POST' }),

  /** `options.objective` also accepts `'auto'` (T-077) — let the manager's
   * rank pick climb/protect/balanced rather than stating one — and
   * `options.leagueId` (T-078) measures effective ownership against a
   * specific mini-league. Both are optional; omitting either leaves analysis
   * exactly as it was before this ticket. */
  analyse: (id: string, options: AnalysisRequestInput = {}) =>
    request<{ analysis: AnalysisResult }>(`/api/squads/${id}/analyse`, {
      method: 'POST',
      body: JSON.stringify(options),
    }),

  /** Where the manager actually stands (T-076) — overall rank, its percentile
   * across the field, gameweeks remaining and mini-league positions, read off
   * the squad's linked FPL entry id. `rank: null` for a squad with none, with
   * `reason` naming why — see `ThisWeek` and `SquadView`'s objective control,
   * both of which must keep working with no standing at all. */
  getRankContext: (squadId: string) =>
    request<{ rank: RankContext | null; reason: string | null }>(`/api/squads/${squadId}/rank`),

  /** A read-only look-back at a past gameweek's picks for an FPL-linked
   * squad (T-129) — `gameweek` must be from 1 up to (not including) the
   * squad's own current gameweek; the server rejects anything outside that
   * range with a clear reason rather than surfacing FPL's own 404 for a
   * gameweek that hasn't been locked in yet. See `SquadHistory`. */
  getSquadHistory: (squadId: string, gameweek: number) =>
    request<{ history: GameweekHistory }>(`/api/squads/${squadId}/history/${gameweek}`),

  /** The multi-week companion to `analyse` — a gameweek-by-gameweek transfer
   * plan over `options.horizon`, deterministic beam search rather than a
   * model call. See `TransferPlanView`. */
  plan: (id: string, options: { horizon?: number; allowHits?: boolean } = {}) =>
    request<{ plan: TransferPlan }>(`/api/squads/${id}/plan`, {
      method: 'POST',
      body: JSON.stringify(options),
    }),

  /** The chip-calendar companion to `plan` — where the squad's own blanks and
   * doubles fall over the horizon, and which gameweek each available chip
   * best suits. Deterministic, same as `plan` — see `ChipCalendar`. */
  getChipPlan: (id: string, options: { horizon?: number } = {}) =>
    request<{ chipPlan: ChipPlan }>(`/api/squads/${id}/chip-plan`, {
      method: 'POST',
      body: JSON.stringify(options),
    }),

  /** One turn of arguing with the verdict — `history` is the conversation so
   * far (never includes `message` itself, the same shape `ChatRequest`
   * expects), the server replies with `reply` plus which deterministic tools
   * it consulted. Stateless on the server, same as `analyse`/`plan`: the
   * client is the one holding the conversation, see `SquadProvider`. */
  chat: (squadId: string, input: { history: ChatMessage[]; message: string }) =>
    request<{ chat: ChatResponse }>(`/api/squads/${squadId}/chat`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  searchPlayers: (query: string) =>
    request<{ players: PlayerSearchResult[] }>(`/api/players/search?q=${encodeURIComponent(query)}`),

  /** Two to four FPL element ids, "Salah or Saka?" answered from real
   * numbers — see `PlayerComparison`. The id-count and validity rules live
   * server-side; this is just the plumbing. */
  comparePlayers: (ids: number[]) =>
    request<{ comparison: PlayerComparison }>(`/api/players/compare?ids=${ids.join(',')}`),

  /** `SquadView`'s "Live points" button calls this on click, and again on
   * every re-click, since scores change during play and a manual refresh is
   * the whole point there. `Tracker` also calls it, but automatically — see
   * that component's own `refresh()` for why this one place breaks the
   * app's usual "never fire a background request a manager didn't ask for"
   * pattern: opening the Tracker section *is* the ask. */
  getLivePoints: (squadId: string) =>
    request<{ points: LivePlayerPoints[]; official: OfficialGameweekPoints | null }>(
      `/api/squads/${squadId}/live-points`,
    ),

  /** Other managers tracked by FPL entry id, independent of squad —
   * `Tracker` is the only caller. */
  getWatchedManagers: () => request<{ managers: WatchedManager[] }>('/api/watched-managers'),

  addWatchedManager: (entryId: number) =>
    request<void>('/api/watched-managers', {
      method: 'POST',
      body: JSON.stringify({ entryId }),
    }),

  removeWatchedManager: (entryId: number) =>
    request<void>(`/api/watched-managers/${entryId}`, { method: 'DELETE' }),

  /** What this tracked rival owns that the most recently loaded squad does
   * not, and vice versa — the server compares against whichever squad it
   * has on file, so this call only makes sense once one is loaded (see
   * `RivalDifferentials`). */
  getDifferentials: (entryId: number) =>
    request<{ comparison: RivalComparison }>(`/api/watched-managers/${entryId}/differentials`),

  /** Fetched automatically by `Tracker` the first time it's opened (and
   * again on manual refresh) — tracked managers' scores change during play
   * just as squad players' do, so the graph needs to start live rather than
   * waiting on an extra click. */
  getWatchedManagersLivePoints: () =>
    request<{ managers: ManagerLivePoints[] }>('/api/watched-managers/live-points'),

  /** Every (entryId, gameweek) reading recorded so far, one per gameweek per
   * manager, plus the signed-in user's own squad-points history alongside it
   * — powers the gameweek-vs-points chart, where "your points" is always
   * plotted, not just tracked managers. Fetched automatically by `Tracker`
   * alongside everything else it loads — see `getWatchedManagersLivePoints`. */
  getWatchedManagersHistory: () =>
    request<{ history: ManagerHistoryPoint[]; ownSquadHistory: OwnSquadHistoryPoint[] }>(
      '/api/watched-managers/history',
    ),

  /** Persists one gameweek's reading of the signed-in user's own
   * squad-points estimate — best-effort, called alongside every live-points
   * refresh so "your points" builds up a real history the same way a
   * watched manager's does, rather than only ever showing the current
   * gameweek. The server, not this call, decides which gameweek it counts
   * for. */
  recordOwnSquadSnapshot: (points: number) =>
    request<void>('/api/watched-managers/own-squad-snapshot', {
      method: 'POST',
      body: JSON.stringify({ points }),
    }),

  /** The signed-in manager's classic mini-leagues (T-054), read off whichever
   * saved squad carries an FPL entry id. `reason` explains an empty list —
   * no imported squad has an entry id yet — rather than the screen just
   * looking empty; see `LeagueStandings`. */
  getLeagues: () =>
    request<{ leagues: LeagueSummary[]; reason: string | null }>('/api/leagues'),

  /** One page of a league's standings, from `leagues-classic/{id}/standings/`
   * — never cached, so re-requesting a page mid-gameweek reflects play in
   * progress. `page` defaults to the first page. */
  getLeagueStandings: (id: number, page = 1) =>
    request<{ standings: LeagueStandingsPage }>(`/api/leagues/${id}/standings?page=${page}`),

  /** Effective ownership across a mini-league's top-ranked rivals (T-078) —
   * how many of them actually hold and captain each player, keyed by FPL
   * element id — not the global `selectedByPercent` figure. See
   * `LeagueStandings`. */
  getEffectiveOwnership: (leagueId: number) =>
    request<{ ownership: EffectiveOwnership; gameweek: number }>(
      `/api/leagues/${leagueId}/effective-ownership`,
    ),

  /** The mini-league read (T-091) surfaced beside its standings (T-092): the
   * template, the league's biggest differential, and every read manager's
   * distance from consensus. See `LeagueInsights`. */
  getLeagueInsights: (leagueId: number) =>
    request<{ insights: LeagueInsights; gameweek: number }>(`/api/leagues/${leagueId}/insights`),

  /** The signed-in manager's own honesty ledger (T-058): every recommendation
   * recorded on analysis, scored once its gameweek is played, most recent
   * first — plus the aggregate `computeAggregate` derives from them. See
   * `AdviceLedger`. */
  getLedger: () =>
    request<{ recommendations: ScoredRecommendation[]; aggregate: LedgerAggregate }>('/api/ledger'),

  /** The honesty ledger aggregated across every user (T-094), no account
   * needed — `PublicLedgerAggregate` itself carries the "not enough scored
   * gameweeks yet" state rather than this call collapsing it into a missing
   * field. See `PublicRecord`. */
  getPublicLedger: () => request<PublicLedgerAggregate>('/api/public/ledger'),

  /** The signed-in user's notification preferences (T-069) — today just the
   * single deadline-briefing opt-in, off by default on every account. See
   * `EmailPreferences`. */
  getPreferences: () => request<{ emailOptIn: boolean }>('/api/preferences'),

  updatePreferences: (emailOptIn: boolean) =>
    request<{ emailOptIn: boolean }>('/api/preferences', {
      method: 'PATCH',
      body: JSON.stringify({ emailOptIn }),
    }),

  /** The unsubscribe link every briefing email carries — deliberately not
   * behind auth server-side (see `preferencesRoutes`), so this is safe to
   * call from a browser with no session at all. Always resolves the same
   * way whether the token was valid, already used, or missing; see
   * `UnsubscribeRoute`. */
  unsubscribe: (token: string) => request<Record<string, never>>(`/api/preferences/unsubscribe?token=${encodeURIComponent(token)}`),

  auth: {
    register: (input: RegisterInput) =>
      request<{ user: User; tokens: AuthTokens }>('/api/auth/register', {
        method: 'POST',
        body: JSON.stringify(input),
      }),

    login: (input: LoginInput) =>
      request<{ user: User; tokens: AuthTokens }>('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify(input),
      }),

    /** Also used internally by the auto-retry-on-401 path — `isRetry: true`
     * so a refresh call that itself 401s fails cleanly instead of recursing. */
    refresh: (token: string) =>
      request<{ tokens: AuthTokens }>(
        '/api/auth/refresh',
        { method: 'POST', body: JSON.stringify({ refreshToken: token } satisfies RefreshInput) },
        { isRetry: true },
      ),

    logout: (token: string) =>
      request<Record<string, never>>('/api/auth/logout', {
        method: 'POST',
        body: JSON.stringify({ refreshToken: token } satisfies RefreshInput),
      }),

    me: () => request<{ user: User }>('/api/auth/me'),

    /** Not a fetch — the browser is redirected to the backend, which in turn
     * redirects to Google. */
    googleSignInUrl: () => `${BASE}/api/auth/google`,
  },
};
